import { FC, useCallback, useEffect, useState } from 'react'
import { useTemplateProjectId } from 'src/query/useTemplateProjectId'
import { CloneTimelineReq, CloneTimelineRes } from 'src/service/OrgSyncService'
import { cloneYearWeek } from 'src/util/time'
import useMigrateStore from './useMigrateStore'
import { useCloneTimeline } from 'src/query/orgMigrate'
import { Spin } from 'antd'
import { useStableCallback } from 'src/hooks/useStableCallback'

interface MigrateCopyTimelineProps {
  migrationRes: (res?: CloneTimelineRes) => void
}

const MigrateCopyTimeline: FC<MigrateCopyTimelineProps> = ({
  migrationRes,
}) => {
  const [req, setReq] = useState<CloneTimelineReq | null>(null)

  const { data, isLoading } = useCloneTimeline(req)

  const { migrateRes, startDate } = useMigrateStore()
  const { destinationProject, selectedTemplate, templateProjectId } =
    useTemplateProjectId()

  const migrateTemplate = useCallback((): CloneTimelineReq => {
    const extractIds = (type: string): number[] =>
      selectedTemplate?.cards
        ?.filter((card) => card.type === type)
        .map((card) => card.taskCtrlId)
        .filter((id): id is number => id !== undefined) || []

    const offset = (() => {
      if (!startDate || !selectedTemplate?.startWeek) return 0
      const startWeekDate = cloneYearWeek(selectedTemplate.startWeek).toDayJs()
      return startDate.diff(startWeekDate, 'day')
    })()

    const keypoint_ids = extractIds('keypoint')
    const delivery_ids = extractIds('delivery')

    return {
      destination_project_id: destinationProject?.id,
      key_point_ids: keypoint_ids,
      delivery_ids: delivery_ids,
      source_project_id: templateProjectId ?? -1,
      offset,
      ...migrateRes,
    }
  }, [
    migrateRes,
    destinationProject,
    selectedTemplate,
    startDate,
    templateProjectId,
  ])

  useEffect(() => {
    setReq(migrateTemplate())
  }, [migrateTemplate])

  const stableSetRes = useStableCallback(migrationRes)

  useEffect(() => {
    if (data) {
      stableSetRes(data)
    }
  }, [data, stableSetRes])

  return (
    <div>
      {isLoading ? (
        <Spin tip="Migrating Timeline..." size="large"></Spin>
      ) : data ? (
        <div className="text-center mt-4">
          <h2 className="text-lg font-semibold">
            Timeline Migration Successful
          </h2>
          <p className="text-sm text-gray-500">
            {data.keypoints_created} keypoints and {data.deliveries_created}{' '}
            deliveries created.
          </p>
        </div>
      ) : (
        <div className="text-center mt-4">
          <h2 className="text-lg font-semibold text-red-600">
            Migration Failed
          </h2>
          <p className="text-sm text-gray-500">
            An error occurred while migrating the timeline. Please try again.
          </p>
        </div>
      )}
    </div>
  )
}

export default MigrateCopyTimeline
